import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MCHttpWrapperService, RECAPTCHA_ERROR_CODES, RESPONSE_STATUS_CODE, MCConfigService, MCRootStoreService, APPLICATIONS_ERROR } from '@maslul/common-services';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { GeneralStore } from '../store/general';

interface RecaptchaError {
    ErrorCode: RECAPTCHA_ERROR_CODES;
}

@Injectable()
export class MIHttpWrapperService extends MCHttpWrapperService {

    constructor(
        protected override http: HttpClient,
        protected override recaptchaV3Service: ReCaptchaV3Service,
        protected override rootStoreService: MCRootStoreService,
        protected override configService: MCConfigService,
        private router: Router,
        private generalStore: GeneralStore
    ) {
        super(http, recaptchaV3Service, rootStoreService, configService);
    }

    private handleRecaptchaError<T>(source$: Observable<T>): Observable<T> {

        const navigateErrorPage = (recaptchaErrorData: RecaptchaError): Observable<never> => {
            const generalErrors = [
                RECAPTCHA_ERROR_CODES.INVALID_TOKEN,
                RECAPTCHA_ERROR_CODES.VALIDATION_FAILD
            ];
            const recaptchaErrors = [
                RECAPTCHA_ERROR_CODES.LOW_SCORE
            ];

            if (generalErrors.includes(recaptchaErrorData.ErrorCode)) {
                this.router.navigate(['/error']);
                return throwError(() => new Error('error handled and redirected.'));
            }
            else if (recaptchaErrors.includes(recaptchaErrorData.ErrorCode)) {
                this.router.navigate(['/recaptcha-error']);
                return throwError(() => new Error('Recaptcha error handled and redirected.'));
            }

            // חייב להחזיר משהו במקרה שלא נכנס לאף IF
            return throwError(() => new Error('General recaptcha error.'));
        };

        const saveErrorPageCode = (recaptchaErrorData: RecaptchaError) => {
            const errorsObj = [
                { recaptchaError: RECAPTCHA_ERROR_CODES.VALIDATION_FAILD, applicationError: APPLICATIONS_ERROR.general },
                { recaptchaError: RECAPTCHA_ERROR_CODES.INVALID_TOKEN, applicationError: APPLICATIONS_ERROR.authorization },
                { recaptchaError: RECAPTCHA_ERROR_CODES.LOW_SCORE, applicationError: APPLICATIONS_ERROR.recaptcha }
            ]
            const applicantErrorCode = errorsObj.find(err => err.recaptchaError === recaptchaErrorData.ErrorCode)?.applicationError || APPLICATIONS_ERROR.general;
            this.generalStore.setErrorPageCode(applicantErrorCode);
        }

        return source$.pipe(
            switchMap((response: any): Observable<T> => {
                if (response?.Code !== RESPONSE_STATUS_CODE.ERROR) {
                    return of(response as T);
                }

                if (response && response.Code === RESPONSE_STATUS_CODE.ERROR && response.Data) {
                    const recaptchaErrorData = response.Data as RecaptchaError;

                    saveErrorPageCode(recaptchaErrorData);
                    return navigateErrorPage(recaptchaErrorData) as unknown as Observable<T>;
                }

                return throwError(() => new Error('Unexpected response flow')) as unknown as Observable<T>;
            })
        );
    }

    override getData<T>(
        url: string,
        showSpinner: boolean = false,
        isDisabledApp?: boolean,
        useRecaptcha: boolean = this.useRecaptcha,
        recaptchaActionName: string = url
    ): Observable<T> {
        // קריאה לשירות הבסיס (MCHttpWrapperService)
        const httpCall = super.getData(
            url,
            showSpinner,
            isDisabledApp,
            useRecaptcha,
            recaptchaActionName
        );
        return this.handleRecaptchaError(httpCall);
    }

    override getCustomData<T>(
        url: string,
        params?: any,
        headers?: HttpHeaders,
        showSpinner: boolean = false,
        isDisabledApp?: boolean,
        useRecaptcha: boolean = this.useRecaptcha,
        recaptchaActionName: string = url
    ): Observable<T> {
        const httpCall = super.getCustomData(
            url,
            params,
            headers,
            showSpinner,
            isDisabledApp,
            useRecaptcha,
            recaptchaActionName
        );
        return this.handleRecaptchaError(httpCall);
    }

    override sendData<T>(
        url: string,
        data: any,
        headers?: HttpHeaders,
        showSpinner: boolean = true,
        isDisabledApp?: boolean,
        useRecaptcha: boolean = this.useRecaptcha,
        recaptchaActionName: string = url
    ): Observable<T> {
        const httpCall = super.sendData(
            url,
            data,
            headers,
            showSpinner,
            isDisabledApp,
            useRecaptcha,
            recaptchaActionName
        ) as Observable<T>;
        return this.handleRecaptchaError(httpCall);
    }

    override  sendFormData<T>(
        url: string,
        data: any,
        showSpinner: boolean = true,
        isDisabledApp?: boolean,
        useRecaptcha: boolean = this.useRecaptcha,
        recaptchaActionName: string = url
    ): Observable<T> {
        const httpCall = super.sendFormData(
            url,
            data,
            showSpinner,
            isDisabledApp,
            useRecaptcha,
            recaptchaActionName
        ) as Observable<T>;
        return this.handleRecaptchaError(httpCall);
    }

    override getBlobData<T>(
        url: string,
        params?: any,
        body: any = null,
        showSpinner: boolean = false,
        isDisabledApp?: boolean,
        useRecaptcha: boolean = this.useRecaptcha,
        recaptchaActionName: string = url
    ): Observable<T> {
        const httpCall = super.getBlobData(
            url,
            params,
            body,
            showSpinner,
            isDisabledApp,
            useRecaptcha,
            recaptchaActionName
        ) as Observable<T>;
        return this.handleRecaptchaError(httpCall);
    }

    override  delete<T>(
        url: string,
        params?: any,
        headers?: HttpHeaders,
        showSpinner: boolean = false,
        isDisabledApp?: boolean,
        useRecaptcha: boolean = this.useRecaptcha,
        recaptchaActionName: string = url
    ): Observable<T> {
        const httpCall = super.delete(
            url,
            params,
            headers,
            showSpinner,
            isDisabledApp,
            useRecaptcha,
            recaptchaActionName
        );
        return this.handleRecaptchaError(httpCall);
    }

    override  put<T>(
        url: string,
        params: any,
        showSpinner: boolean = false,
        isDisabledApp?: boolean,
        useRecaptcha: boolean = this.useRecaptcha,
        recaptchaActionName: string = url
    ): Observable<T> {
        const httpCall = super.put(
            url,
            params,
            showSpinner,
            isDisabledApp,
            useRecaptcha,
            recaptchaActionName
        );
        return this.handleRecaptchaError(httpCall);
    }
}